import React, { useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { apiService } from '../services/api';
import type {
  GoogleAdsAdGroup,
  GoogleAdsAdGroupFailure,
  GoogleAdsAssociationResponse,
  GoogleAdsAssociationRequest,
  GoogleAdsCampaign,
  GoogleAdsUploadError,
  GoogleAdsStoredAssociation,
} from '../services/api';

interface GoogleAdsAssociationDialogProps {
  adId: string;
  open: boolean;
  onClose: () => void;
  onCompleted: (response: GoogleAdsAssociationResponse) => void;
}

const getErrorMessage = (error: unknown): string => (
  error instanceof Error && error.message ? error.message : 'Google Ads request failed'
);

/** Mirrors the backend sentence, used when a response marks a placement locked without a reason. */
const REMOVAL_BLOCKED_FALLBACK = "This is the ad group's only video asset, so removing it would leave the App ad without a video.";

/**
 * Google Ads display names are not unique within an account, so two live placements can share one
 * campaign/ad group name pair. Every placement control is named with the ids as well, which are
 * unique, so an operator can always tell which placement they are about to attach or unlink.
 */
const placementName = (
  campaignText: string,
  campaignId: string,
  adGroupText: string,
  adGroupId: string,
): string => `${campaignText} (${campaignId}) · ${adGroupText} (${adGroupId})`;

interface SaveFailures {
  additions: GoogleAdsAdGroupFailure[];
  removals: GoogleAdsAdGroupFailure[];
  message: string | null;
}
type PlacementFailureError = Pick<
  GoogleAdsUploadError,
  'failed_ad_groups' | 'failed_removed_ad_groups'
>;

const hasPlacementFailures = (error: unknown): error is PlacementFailureError => {
  if (typeof error !== 'object' || error === null) return false;
  const candidate = error as PlacementFailureError;
  return (Array.isArray(candidate.failed_ad_groups) && candidate.failed_ad_groups.length > 0)
    || (Array.isArray(candidate.failed_removed_ad_groups) && candidate.failed_removed_ad_groups.length > 0);
};

/** Reads the backend error code the api client attaches to a rejected Google Ads request. */
const readErrorCode = (error: unknown): string | null => {
  if (typeof error !== 'object' || error === null) return null;
  const code = (error as Record<string, unknown>).code;
  return typeof code === 'string' && code ? code : null;
};

const GoogleAdsAssociationDialog: React.FC<GoogleAdsAssociationDialogProps> = ({
  adId,
  open,
  onClose,
  onCompleted,
}) => {
  const [campaigns, setCampaigns] = useState<GoogleAdsCampaign[]>([]);
  const [adGroupsByCampaign, setAdGroupsByCampaign] = useState<Record<string, GoogleAdsAdGroup[]>>({});
  const [loadingAdGroups, setLoadingAdGroups] = useState<Record<string, boolean>>({});
  const [existingAssociations, setExistingAssociations] = useState<GoogleAdsStoredAssociation[]>([]);
  const [selected, setSelected] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adGroupErrors, setAdGroupErrors] = useState<Record<string, string>>({});
  const [saveFailures, setSaveFailures] = useState<SaveFailures | null>(null);
  const [baselineLoaded, setBaselineLoaded] = useState(false);
  const [assetMissing, setAssetMissing] = useState(false);
  // Ad group loads outlive the dialog session that started them. Every session bumps this token so
  // a response from a closed or re-targeted dialog cannot land in the reopened one.
  const sessionRef = useRef(0);

  const existingGroups = Array.from(new Map(
    existingAssociations.map(association => [
      `${association.campaign_id}:${association.ad_group_id}`,
      association,
    ]),
  ).values());
  const existingKeys = new Set(
    existingGroups.map(association => `${association.campaign_id}:${association.ad_group_id}`),
  );
  // A placement the backend marked non-removable carries the ad group's only video asset. Removing
  // it would leave the App ad without a video, so the row is locked and the reason is shown.
  const lockedRemovals = new Map(
    existingGroups
      .filter(association => association.removable === false)
      .map(association => [
        `${association.campaign_id}:${association.ad_group_id}`,
        association.removal_blocked_reason || REMOVAL_BLOCKED_FALLBACK,
      ]),
  );
  useEffect(() => {
    sessionRef.current += 1;
    setCampaigns([]);
    setAdGroupsByCampaign({});
    setLoadingAdGroups({});
    setExistingAssociations([]);
    setSelected({});
    setError(null);
    setAdGroupErrors({});
    setSaveFailures(null);
    setAssetMissing(false);
    setBaselineLoaded(false);
    setSaving(false);
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    // The two loads are independent: an ad whose stored asset is gone from the account still needs
    // the campaign list, both to show which advertiser account is being inspected and to keep the
    // missing-asset notice attributable to the asset rather than to a dialog-wide load failure.
    const loadCampaigns = apiService.getGoogleAdsCampaigns().then(response => {
      if (!cancelled) setCampaigns(response.campaigns);
    });
    const loadAssociations = apiService.getGoogleAdsAssociations(adId)
      .then(response => {
        if (cancelled) return;
        setExistingAssociations(response.associations);
        setSelected(response.associations.reduce<Record<string, string[]>>((current, association) => ({
          ...current,
          [association.campaign_id]: [
            ...(current[association.campaign_id] ?? []),
            association.ad_group_id,
          ],
        }), {}));
        setBaselineLoaded(true);
      })
      .catch((loadError: unknown) => {
        // A missing asset is an empty baseline, not a dialog failure: there is nothing to remove,
        // and placement cannot create the asset, so the notice below tells the operator to upload
        // the creative to the library again first.
        if (readErrorCode(loadError) !== 'GOOGLE_ADS_ASSET_NOT_FOUND') throw loadError;
        if (cancelled) return;
        setAssetMissing(true);
        setBaselineLoaded(true);
      });
    Promise.all([loadCampaigns, loadAssociations])
      .catch((loadError: unknown) => {
        if (!cancelled) setError(getErrorMessage(loadError));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      sessionRef.current += 1;
    };
  }, [open, adId]);

  const toggleCampaign = async (campaignId: string, expanded: boolean): Promise<void> => {
    if (!expanded || adGroupsByCampaign[campaignId] || loadingAdGroups[campaignId]) return;
    const session = sessionRef.current;
    setLoadingAdGroups(current => ({ ...current, [campaignId]: true }));
    try {
      const response = await apiService.getGoogleAdsAdGroups(campaignId);
      if (session !== sessionRef.current) return;
      setAdGroupsByCampaign(current => ({ ...current, [campaignId]: response.ad_groups }));
      setAdGroupErrors(current => {
        if (!(campaignId in current)) return current;
        const next = { ...current };
        delete next[campaignId];
        return next;
      });
    } catch (loadError: unknown) {
      if (session !== sessionRef.current) return;
      setAdGroupErrors(current => ({ ...current, [campaignId]: getErrorMessage(loadError) }));
    } finally {
      if (session === sessionRef.current) {
        setLoadingAdGroups(current => ({ ...current, [campaignId]: false }));
      }
    }
  };

  const toggleAdGroup = (campaignId: string, adGroupId: string): void => {
    if (saving || lockedRemovals.has(`${campaignId}:${adGroupId}`)) return;
    setSelected(current => {
      const ids = current[campaignId] ?? [];
      const nextIds = ids.includes(adGroupId)
        ? ids.filter(id => id !== adGroupId)
        : [...ids, adGroupId];
      const next = { ...current };
      if (nextIds.length) next[campaignId] = nextIds;
      else delete next[campaignId];
      return next;
    });
  };

  const newSelections = Object.entries(selected)
    .map(([campaign_id, ids]) => ({
      campaign_id,
      ad_group_ids: ids.filter(adGroupId => !existingKeys.has(`${campaign_id}:${adGroupId}`)),
    }))
    .filter(selection => selection.ad_group_ids.length > 0);
  const removalSelections = existingGroups.reduce<Record<string, string[]>>((current, association) => {
    const remainsSelected = (selected[association.campaign_id] ?? []).includes(association.ad_group_id);
    const locked = lockedRemovals.has(`${association.campaign_id}:${association.ad_group_id}`);
    if (remainsSelected || locked) return current;
    return {
      ...current,
      [association.campaign_id]: [
        ...(current[association.campaign_id] ?? []),
        association.ad_group_id,
      ],
    };
  }, {});
  const removalRequests = Object.entries(removalSelections).map(([campaign_id, ad_group_ids]) => ({
    campaign_id,
    ad_group_ids,
  }));
  const additionCount = newSelections.reduce((total, selection) => total + selection.ad_group_ids.length, 0);
  const removalCount = removalRequests.reduce((total, selection) => total + selection.ad_group_ids.length, 0);
  const changeCount = additionCount + removalCount;

  const campaignLabel = (campaignId: string): string => {
    const campaign = campaigns.find(item => item.campaign_id === campaignId);
    if (campaign?.name) return campaign.name;
    const association = existingGroups.find(item => item.campaign_id === campaignId);
    return association?.campaign_name || `Campaign ${campaignId}`;
  };

  const adGroupLabel = (campaignId: string, adGroupId: string): string => {
    const adGroup = (adGroupsByCampaign[campaignId] ?? []).find(item => item.ad_group_id === adGroupId);
    if (adGroup?.name) return adGroup.name;
    const association = existingGroups.find(item => (
      item.campaign_id === campaignId && item.ad_group_id === adGroupId
    ));
    return association?.ad_group_name || `Ad group ${adGroupId}`;
  };

  const failureRows = saveFailures
    ? [
      ...saveFailures.additions.map(failure => ({ action: 'Add failed', failure })),
      ...saveFailures.removals.map(failure => ({ action: 'Removal failed', failure })),
    ]
    : [];

  /**
   * Rebases the baseline onto what the response reports as live. Placements the backend attached or
   * detached become the operator's new starting point, so a partial save leaves only the failures in
   * the diff: what just succeeded is reversible, and a retry cannot resubmit completed work.
   */
  const applyResponseToBaseline = (response: GoogleAdsAssociationResponse): void => {
    const attached = [...response.successful_ad_groups, ...response.already_uploaded_ad_groups];
    const detached = [...response.removed_ad_groups, ...(response.already_removed_ad_groups ?? [])];
    if (!attached.length && !detached.length) return;
    const keyOf = (placement: { campaign_id: string; ad_group_id: string }): string => (
      `${placement.campaign_id}:${placement.ad_group_id}`
    );
    const detachedKeys = new Set(detached.map(keyOf));
    setExistingAssociations(current => {
      const next = new Map(current.map(association => [keyOf(association), association]));
      detachedKeys.forEach(key => next.delete(key));
      attached.forEach(placement => {
        const key = keyOf(placement);
        if (next.has(key)) return;
        const adGroup = (adGroupsByCampaign[placement.campaign_id] ?? [])
          .find(item => item.ad_group_id === placement.ad_group_id);
        if (!adGroup) return;
        // The backend does not report removability for a fresh placement, so the row stays
        // unlocked: a removal it will not allow comes back as a failure the operator can read.
        next.set(key, {
          campaign_id: placement.campaign_id,
          ad_group_id: placement.ad_group_id,
          ad_group_resource_name: adGroup.resource_name,
          campaign_name: campaignLabel(placement.campaign_id),
          ad_group_name: adGroupLabel(placement.campaign_id, placement.ad_group_id),
        });
      });
      return Array.from(next.values());
    });
    setSelected(current => {
      const next: Record<string, string[]> = {};
      Object.entries(current).forEach(([campaignId, adGroupIds]) => {
        const kept = adGroupIds.filter(adGroupId => !detachedKeys.has(`${campaignId}:${adGroupId}`));
        if (kept.length) next[campaignId] = kept;
      });
      attached.forEach(placement => {
        const adGroupIds = next[placement.campaign_id] ?? [];
        if (!adGroupIds.includes(placement.ad_group_id)) {
          next[placement.campaign_id] = [...adGroupIds, placement.ad_group_id];
        }
      });
      return next;
    });
  };

  const save = async (): Promise<void> => {
    if (!baselineLoaded || !changeCount || saving) return;
    const session = sessionRef.current;
    setSaving(true);
    setError(null);
    setSaveFailures(null);
    const request: GoogleAdsAssociationRequest = newSelections.length > 0
      ? {
        campaign_ad_groups: [newSelections[0], ...newSelections.slice(1)],
        ...(removalRequests.length > 0
          ? { remove_campaign_ad_groups: [removalRequests[0], ...removalRequests.slice(1)] }
          : {}),
      }
      : { remove_campaign_ad_groups: [removalRequests[0], ...removalRequests.slice(1)] };
    try {
      const response = await apiService.associateGoogleAdsAsset(adId, request);
      if (session !== sessionRef.current) return;
      if (
        response.status === 'partial_success'
        || response.failed_ad_groups.length
        || response.failed_removed_ad_groups.length
      ) {
        setSaveFailures({
          additions: response.failed_ad_groups,
          removals: response.failed_removed_ad_groups,
          message: response.message || null,
        });
      }
      // The response completed a round trip, so the parent hears about it either way; the dialog
      // stays open on a partial result with the failure list still on screen.
      applyResponseToBaseline(response);
      onCompleted(response);
    } catch (saveError: unknown) {
      if (session !== sessionRef.current) return;
      setError(getErrorMessage(saveError));
      setSaveFailures(hasPlacementFailures(saveError) ? {
        additions: saveError.failed_ad_groups ?? [],
        removals: saveError.failed_removed_ad_groups ?? [],
        message: null,
      } : null);
    } finally {
      if (session === sessionRef.current) setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create Google Ads associations</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {saveFailures && (
            <Alert severity="warning">
              <Typography variant="body2">Some placements were not updated</Typography>
              {saveFailures.message && (
                <Typography variant="caption" component="div">{saveFailures.message}</Typography>
              )}
              {failureRows.map(({ action, failure }) => (
                <Typography
                  key={`${action}:${failure.campaign_id}:${failure.ad_group_id}`}
                  variant="caption"
                  component="div"
                >
                  {`${action}: ${campaignLabel(failure.campaign_id)} · ${adGroupLabel(failure.campaign_id, failure.ad_group_id)} — ${failure.error}`}
                </Typography>
              ))}
            </Alert>
          )}
          {assetMissing && (
            <Alert severity="info" data-testid="google-ads-asset-missing">
              This ad&apos;s Google Ads asset no longer exists in the current advertiser account, so it
              has no associations. Upload the creative to the Google Ads library again before placing it
              in ad groups.
            </Alert>
          )}
          {existingGroups.length > 0 && (
            <Box>
              <Alert severity="info" sx={{ mb: 1 }}>
                <Typography variant="body2">Current associations</Typography>
                <Typography variant="caption" component="div">
                  Uncheck a placement to remove it from the live campaign.
                </Typography>
              </Alert>
              <List dense disablePadding aria-label="Current associations">
                {existingGroups.map(association => {
                  const associationKey = `${association.campaign_id}:${association.ad_group_id}`;
                  const checked = (selected[association.campaign_id] ?? []).includes(association.ad_group_id);
                  const lockedReason = lockedRemovals.get(associationKey) ?? null;
                  const campaignText = association.campaign_name || `Campaign ${association.campaign_id}`;
                  const adGroupText = association.ad_group_name || `Ad group ${association.ad_group_id}`;
                  const associationLabel = `${campaignText} · ${adGroupText}`;
                  const associationName = placementName(
                    campaignText,
                    association.campaign_id,
                    adGroupText,
                    association.ad_group_id,
                  );
                  return (
                    <ListItem
                      key={associationKey}
                      disableGutters
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <Checkbox
                        checked={checked}
                        size="small"
                        disabled={saving || lockedReason !== null}
                        onChange={() => toggleAdGroup(association.campaign_id, association.ad_group_id)}
                        inputProps={{ 'aria-label': associationName }}
                      />
                      <Typography variant="caption" sx={{ flex: 1 }}>
                        {associationLabel}
                      </Typography>
                      {lockedReason !== null ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          data-testid={`google-ads-removal-locked-${associationKey}`}
                        >
                          {lockedReason}
                        </Typography>
                      ) : !checked && (
                        <Typography variant="caption" color="warning.main">
                          Will remove from live campaign
                        </Typography>
                      )}
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          )}
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography>Loading campaigns...</Typography>
            </Box>
          ) : campaigns.length === 0 ? (
            <Typography color="text.secondary">No enabled App campaigns found.</Typography>
          ) : campaigns.map(campaign => (
            <Accordion
              key={campaign.campaign_id}
              disabled={saving}
              onChange={(_, expanded) => void toggleCampaign(campaign.campaign_id, expanded)}
              variant="outlined"
              disableGutters
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ width: '100%' }}>
                  <Typography>{campaign.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{campaign.campaign_id}</Typography>
                  {existingAssociations.some(item => item.campaign_id === campaign.campaign_id) && (
                    <Typography variant="caption" color="primary" component="div">
                      {existingAssociations.filter(item => item.campaign_id === campaign.campaign_id).length} current association(s)
                    </Typography>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {loadingAdGroups[campaign.campaign_id] ? (
                  <CircularProgress size={20} />
                ) : adGroupErrors[campaign.campaign_id] ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography color="error">
                      {`Could not load ad groups: ${adGroupErrors[campaign.campaign_id]}`}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => void toggleCampaign(campaign.campaign_id, true)}
                    >
                      Retry
                    </Button>
                  </Box>
                ) : (adGroupsByCampaign[campaign.campaign_id] ?? []).length === 0 ? (
                  <Typography color="text.secondary">No enabled ad groups found.</Typography>
                ) : (
                  <List dense disablePadding aria-label={`Ad groups in ${campaign.name} (${campaign.campaign_id})`}>
                    {(adGroupsByCampaign[campaign.campaign_id] ?? []).map(adGroup => {
                      const associationKey = `${campaign.campaign_id}:${adGroup.ad_group_id}`;
                      const isExisting = existingKeys.has(associationKey);
                      const checked = (selected[campaign.campaign_id] ?? []).includes(adGroup.ad_group_id);
                      const isRemovalQueued = isExisting && !checked;
                      const disabled = saving
                        || lockedRemovals.has(associationKey)
                        || (!isExisting && adGroup.app_ad_count === 0);
                      // The row is the control: it carries the checkbox role and state so a
                      // keyboard or screen reader operator hears which placement is selected, and
                      // it describes itself with the ad group id and status text the label hides.
                      const detailId = `google-ads-ad-group-detail-${associationKey}`;
                      const removalNoticeId = `google-ads-ad-group-removal-${associationKey}`;
                      return (
                        <ListItemButton
                          key={adGroup.ad_group_id}
                          disabled={disabled}
                          role="checkbox"
                          aria-checked={checked}
                          aria-label={placementName(
                            campaign.name,
                            campaign.campaign_id,
                            adGroup.name,
                            adGroup.ad_group_id,
                          )}
                          aria-describedby={isRemovalQueued ? `${detailId} ${removalNoticeId}` : detailId}
                          // ButtonBase activates a non-native button on space keyup without
                          // checking `disabled`, so a disabled row keeps no handler at all: a
                          // screen reader operator cannot toggle a placement the row refuses.
                          onClick={disabled
                            ? undefined
                            : () => toggleAdGroup(campaign.campaign_id, adGroup.ad_group_id)}
                        >
                          <ListItemIcon>
                            <Checkbox
                              checked={checked}
                              disabled={disabled}
                              tabIndex={-1}
                              inputProps={{ 'aria-hidden': true }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={adGroup.name}
                            secondary={isExisting
                              ? `${adGroup.ad_group_id} · Currently associated`
                              : adGroup.app_ad_count === 0
                                ? `${adGroup.ad_group_id} · Create an App ad in this ad group first`
                                : adGroup.ad_group_id}
                            secondaryTypographyProps={{ id: detailId }}
                          />
                          {isRemovalQueued && (
                            <Typography
                              id={removalNoticeId}
                              variant="caption"
                              color="warning.main"
                              sx={{ ml: 2, textAlign: 'right' }}
                            >
                              Will remove from live campaign
                            </Typography>
                          )}
                        </ListItemButton>
                      );
                    })}
                  </List>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={() => void save()} disabled={!baselineLoaded || !changeCount || saving}>
          {saving ? 'Saving...' : `Save${changeCount ? ` (${changeCount})` : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GoogleAdsAssociationDialog;
